import { map } from 'ramda';
import PCNode from './Nodes/PCNode';
import PCSpool from './Spools/PCSpool';
import PCStope from './Stopes/PCStope';
import { isSpoolShown } from './Spools/utils';
import { isSpoolDateFilterActive, SpoolDateFilter, spoolMatchesDateFilter } from './spoolDateFilter';

// The level and area selection. Stopes are filtered out of the model; nodes and spools only when drawn, because
// routing, recipes and exports keep reading the whole of model.nodes and model.spools.
export interface LevelAreaFilter {
  levels: string[];
  areas: string[];
}

export const NO_LEVEL_AREA_FILTER: LevelAreaFilter = { levels: [], areas: [] };

export const isLevelAreaFilterActive = (filter: LevelAreaFilter): boolean => filter.levels.length > 0 || filter.areas.length > 0;

// An empty selection passes everything; '' in a selection is the "no value" option, as in the stope filter.
const passes = (selected: string[], value?: string | null): boolean => selected.length === 0 || selected.includes(value ?? '');

export const nodeMatchesLevelArea = (node: PCNode, filter: LevelAreaFilter): boolean => passes(filter.levels, node.level) && passes(filter.areas, node.area);

// A stope without its own tag goes by its node, as an untagged spool goes by its ends.
export const stopeMatchesLevelArea = (stope: PCStope, filter: LevelAreaFilter): boolean => (
  passes(filter.levels, stope.data?.level || stope.originNode?.level) && passes(filter.areas, stope.data?.area || stope.originNode?.area)
);

export const filterStopesByLevelArea = (filter: LevelAreaFilter) => (stopes: Record<string, PCStope[]>): Record<string, PCStope[]> => (
  map((group: PCStope[]) => group.filter((stope) => stopeMatchesLevelArea(stope, filter)), stopes)
);

// A spool is judged by its own tag. An untagged one - typically a borehole between two levels - by either end node,
// so a connection leaving the selected level stays visible.
const spoolTagPasses = (selected: string[], own: string | undefined, a: string | undefined, b: string | undefined): boolean => (
  own ? passes(selected, own) : passes(selected, a) || passes(selected, b)
);

export const spoolMatchesLevelArea = (spool: PCSpool, filter: LevelAreaFilter): boolean => (
  spoolTagPasses(filter.levels, spool.level, spool.pointA?.level, spool.pointB?.level)
  && spoolTagPasses(filter.areas, spool.area, spool.pointA?.area, spool.pointB?.area)
);

// The view settings that decide which of the network is drawn; the model carries all three, so it can be passed as is.
export interface NetworkView {
  showInactiveSpools: boolean;
  levelAreaFilter: LevelAreaFilter;
  spoolDateFilter: SpoolDateFilter;
}

// Whether a spool exists in the view at all: its status and the date filter.
const isSpoolInView = (spool: PCSpool, view: NetworkView): boolean => (
  isSpoolShown(spool, view.showInactiveSpools) && spoolMatchesDateFilter(spool, view.spoolDateFilter)
);

export const isSpoolVisible = (spool: PCSpool, view: NetworkView): boolean => (
  isSpoolInView(spool, view) && spoolMatchesLevelArea(spool, view.levelAreaFilter)
);

const spoolEndIds = (spools: PCSpool[]): string[] => spools.flatMap((spool) => [spool.pointA.id, spool.pointB.id]);

// Nodes whose spools are all hidden by status or date. A stope hangs off its node's lines, so it - and its shape - is
// hidden with them. Level and area stay out of it: there a stope is judged by its own tags. A node with no spools is
// never on the list.
export const hiddenLineNodeIds = (spools: PCSpool[], view: NetworkView): Set<string> => {
  if (view.showInactiveSpools && !isSpoolDateFilterActive(view.spoolDateFilter)) return new Set<string>();
  const shown = new Set(spoolEndIds(spools.filter((spool) => isSpoolInView(spool, view))));
  return new Set(spoolEndIds(spools).filter((id) => !shown.has(id)));
};

// A node with spools is drawn only while one of them is, so hiding inactive spools or filtering by level leaves no
// stranded dots. A node with no spools is judged by its own tags, and a drawn stope always keeps its node.
export const visibleNodes = (
  nodes: PCNode[],
  spools: PCSpool[],
  shownSpools: PCSpool[],
  filter: LevelAreaFilter,
  keepNodeIds: string[] = [],
): PCNode[] => {
  if (!isLevelAreaFilterActive(filter) && shownSpools.length === spools.length) return nodes;
  const connected = new Set(spoolEndIds(spools));
  const kept = new Set([...keepNodeIds, ...spoolEndIds(shownSpools)]);
  return nodes.filter((node) => kept.has(node.id) || (!connected.has(node.id) && nodeMatchesLevelArea(node, filter)));
};

export interface VisibleLines {
  spools: PCSpool[];
  hiddenStopeNodeIds: Set<string>;
}

// The drawn spools and the nodes they leave without a line. Neither reads the nodes, so the renderer memoises them apart:
// the spool mesh rebuilds whenever its array changes, and node colours change often.
export const visibleLines = (spools: PCSpool[], view: NetworkView): VisibleLines => ({
  spools: spools.filter((spool) => isSpoolVisible(spool, view)),
  hiddenStopeNodeIds: hiddenLineNodeIds(spools, view),
});

export interface VisibleNetwork extends VisibleLines {
  stopes: PCStope[];
  nodes: PCNode[];
}

// What the 3D view draws and picks, worked out in one place for the renderer and marquee selection so they never disagree.
export const visibleNetwork = (
  nodes: PCNode[],
  spools: PCSpool[],
  stopes: PCStope[],
  view: NetworkView,
  lines: VisibleLines = visibleLines(spools, view),
): VisibleNetwork => {
  const nodeIds = new Set(nodes.map((node) => node.id));
  const shownStopes = stopes.filter((stope) => !!stope?.originNode && nodeIds.has(stope.originNode.id) && !lines.hiddenStopeNodeIds.has(stope.originNode.id));
  return {
    ...lines,
    stopes: shownStopes,
    nodes: visibleNodes(nodes, spools, lines.spools, view.levelAreaFilter, shownStopes.map((stope) => stope.originNode.id)),
  };
};

const distinctNonEmpty = (values: (string | undefined | null)[]): string[] => (
  Array.from(new Set(values.filter((value): value is string => !!value))).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
);

// Every level and area the stopes, nodes and spools carry, so a level with no stopes on it can still be chosen.
export const levelAreaOptions = (
  stopes: { level?: string | null; area?: string | null }[],
  nodes: PCNode[],
  spools: PCSpool[],
): { levelOptions: string[]; areaOptions: string[] } => ({
  levelOptions: distinctNonEmpty([...stopes.map((s) => s.level), ...nodes.map((n) => n.level), ...spools.map((s) => s.level)]),
  areaOptions: distinctNonEmpty([...stopes.map((s) => s.area), ...nodes.map((n) => n.area), ...spools.map((s) => s.area)]),
});
